/**
 * ContractOverview — ultra-detailed contract analytics dashboard for a single contract.
 * Pulls: contract.ai_analysis, latest Cognara AI review (clmAIReviews), plus contract-scoped
 * roll-ups from claims / LD / variations / obligations / correspondences.
 */
import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Sparkles, ShieldAlert, Target, FileWarning, Scale, TrendingDown, Bot, Gavel, Timer, GitBranch, Mail, BookOpen, ClipboardList, DollarSign, AlertTriangle, Calendar, FileText, ArrowUpRight, CheckCircle2, MapPin } from 'lucide-react';
import api from '@/lib/api';
import { RiskSourceDialog } from './RiskSourceDialog';
import { ClaimOpportunitiesTable, LdAnalysisPanel, VariationsAnalysisPanel, DisputesAnalysisPanel } from './CognaraClaimPanels';
import { CognaraReviewProvider } from './CognaraComponentChat';

const sev = (s) => ({
  CRITICAL: { bg: '#EF444422', color: '#F87171' },
  HIGH:     { bg: '#F59E0B22', color: '#FBBF24' },
  MEDIUM:   { bg: '#FCD34D22', color: '#FCD34D' },
  MED:      { bg: '#FCD34D22', color: '#FCD34D' },
  LOW:      { bg: '#22C55E22', color: '#4ADE80' },
  P0:       { bg: '#EF444422', color: '#F87171' },
  P1:       { bg: '#F59E0B22', color: '#FBBF24' },
  P2:       { bg: '#22C55E22', color: '#4ADE80' },
})[s] || { bg: '#94A3B822', color: '#CBD5E1' };
const riskColor = (s) => s == null ? '#94A3B8' : s <= 30 ? '#22C55E' : s <= 60 ? '#F59E0B' : '#EF4444';
const scoreColor = (s) => s == null ? '#94A3B8' : s >= 75 ? '#22C55E' : s >= 55 ? '#F59E0B' : '#EF4444';

export function ContractOverview({ contract, contractId, clauses, obligations, claims, ldTrackers, variations, disputes, correspondences, onGoto, formatINR, formatDate }) {
  const [aiReview, setAiReview] = useState(null);
  const [aiReviewId, setAiReviewId] = useState(null);
  const [sourceRisk, setSourceRisk] = useState(null);

  useEffect(() => { (async () => {
    try {
      const r = await api.get(`/clm/contracts/${contractId}/ai-reviews?limit=1`);
      const list = r.data?.reviews || [];
      if (list.length) { setAiReview(list[0].review); setAiReviewId(list[0].id); }
    }
    catch {}
  })(); }, [contractId]);

  // Contract-scoped roll-ups
  const rollup = useMemo(() => {
    const openClaims = claims.filter(c => !['SETTLED', 'CLOSED', 'REJECTED'].includes(c.status));
    return {
      claim_exposure: openClaims.reduce((s, c) => s + (c.requested_amount || 0), 0),
      // The calculator writes `current_exposure`; older rows wrote `current_ld_amount`.
      ld_exposure: ldTrackers.reduce((s, t) => s + (t.computed?.current_exposure ?? t.computed?.current_ld_amount ?? 0), 0),
      // The cap is a contract-wide ceiling, not the sum of the per-milestone caps.
      ld_cap: contract.ld_terms?.cap_amount || Math.max(0, ...ldTrackers.map(t => t.cap_amount || 0)),
      variation_value: variations.reduce((s, v) => s + (v.requested_amount || v.value || 0), 0),
      overdue_obligations: obligations.filter(o => o.status === 'OVERDUE').length,
      urgent_correspondences: correspondences.filter(c => c.sla_status === 'BREACHED' || c.sla_status === 'URGENT').length,
    };
  }, [claims, ldTrackers, variations, obligations, correspondences, contract]);

  const ai = contract.ai_analysis || {};
  const fin = contract.financials || {};
  const value = fin.original_value || contract.original_value || 0;
  const ldCapPct = contract.ld_terms?.cap_percent ?? fin.ld_cap_percentage ?? fin.ld_cap_pct ?? null;

  // Prefer the latest Cognara AI Review's richer fields over ai_analysis
  const risks = aiReview?.risks || ai.risks || (ai.factors || []).map((f, i) => ({ id: `R${i + 1}`, title: f.factor, category: f.category || 'GENERAL', likelihood: f.likelihood || 3, impact: f.impact || 3, score: f.score || (f.severity === 'CRITICAL' ? 20 : f.severity === 'HIGH' ? 15 : 8), exposure: f.explanation, mitigation: f.mitigation || '—', owner: f.owner || 'PM', priority: f.severity === 'CRITICAL' ? 'P0' : f.severity === 'HIGH' ? 'P1' : 'P2' }));
  const levers = aiReview?.negotiationLevers || [];
  const clausesGaps = aiReview?.missingOrWeakClauses || [];
  const claimsExp = aiReview?.claimsExposure || [];
  const cashflow = aiReview?.cashflowReview;
  const compliance = aiReview?.complianceFit;
  const actions = aiReview?.recommendedActions || [];
  const claimOpps = aiReview?.claimOpportunities || [];
  const ldAnalysis = aiReview?.ldAnalysis;
  const variationsAnalysis = aiReview?.variationsAnalysis;
  const disputesAnalysis = aiReview?.disputesAnalysis;
  const cognaraScore = aiReview?.cognaraScore;

  // Risk category heat-map
  const heat = useMemo(() => {
    const bucket = {};
    for (const r of (risks || [])) {
      const k = (r.category || 'GENERAL').toUpperCase();
      bucket[k] = bucket[k] || { count: 0, score: 0 };
      bucket[k].count += 1;
      bucket[k].score += (r.score || 0);
    }
    return Object.entries(bucket).map(([k, v]) => ({ category: k, count: v.count, avgScore: Math.round(v.score / v.count) })).sort((a, b) => b.avgScore - a.avgScore);
  }, [risks]);

  return (
    <CognaraReviewProvider value={{ contractId, reviewId: aiReviewId }}>
    <div className="space-y-6" data-testid="contract-overview">
      {/* Top KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
        {[
          { label: 'Value', value: formatINR(value), sub: `${fin.retention_percent || fin.retention_percentage || 5}% ret.`, color: '#10B981', icon: DollarSign },
          { label: 'Risk Score', value: ai.risk_score ?? '—', sub: 'AI-derived', color: riskColor(ai.risk_score), icon: ShieldAlert },
          { label: 'Cognara Score', value: cognaraScore ?? '—', sub: aiReview?.verdict || 'Not run', color: scoreColor(cognaraScore), icon: Sparkles },
          { label: 'Clauses', value: clauses.length, sub: `${clausesGaps.length} gaps`, color: '#3B82F6', icon: BookOpen },
          { label: 'Open Claims', value: claims.filter(c => !['SETTLED', 'CLOSED', 'REJECTED'].includes(c.status)).length, sub: formatINR(rollup.claim_exposure), color: '#F59E0B', icon: Gavel },
          { label: 'Claim Opps', value: claimOpps.length || '—', sub: claimOpps.length ? `${claimOpps.filter(c => !c.alreadyRaised).length} unraised` : 'Not scanned', color: '#14B8A6', icon: Target },
          { label: 'LD Exposure', value: formatINR(rollup.ld_exposure), sub: rollup.ld_cap ? `${Math.round(rollup.ld_exposure / rollup.ld_cap * 100)}% of cap` : 'No cap', color: '#EF4444', icon: Timer },
          { label: 'Variations', value: variations.length, sub: formatINR(rollup.variation_value), color: '#8B5CF6', icon: GitBranch },
          { label: 'Time-bar Alerts', value: rollup.urgent_correspondences, sub: `${correspondences.length} letters`, color: '#F97316', icon: Mail },
        ].map(k => (
          <Card key={k.label} className="bg-card border-border"><CardContent className="p-3">
            <div className="flex items-center gap-1.5 mb-1"><k.icon className="w-3.5 h-3.5" style={{ color: k.color }} /><span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">{k.label}</span></div>
            <p className="text-lg font-bold" style={{ color: k.color }}>{k.value}</p>
            <p className="text-[10px] text-muted-foreground/70">{k.sub}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contract Information + Documents (spans 2) */}
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><FileText className="w-4 h-4 text-blue-400" />Contract Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Type" value={contract.contract_type} />
              <Field label="Status" value={<Badge variant="outline" className="text-[10px]">{contract.status || 'DRAFT'}</Badge>} />
              <Field label="Start" value={formatDate(contract.dates?.commencement_date || contract.dates?.effective_date || contract.start_date || contract.dates?.start_date)} />
              <Field label="Completion" value={formatDate(contract.dates?.completion_date || contract.end_date)} />
              <Field label="Value" value={formatINR(value)} />
              <Field label="Current Value" value={formatINR(fin.current_value || value)} />
              <Field label="Currency" value={fin.currency || 'INR'} />
              <Field label="Retention" value={`${fin.retention_percent || fin.retention_percentage || 5}%`} />
              <Field label="LD Cap" value={ldCapPct == null ? '—' : `${ldCapPct}%`} />
            </div>
            {contract.parties && Object.keys(contract.parties).length > 0 && (
              <div className="pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground/70 uppercase mb-2 tracking-wider">Parties</p>
                <div className="grid grid-cols-2 gap-3">
                  {contract.parties.employer && <div className="p-3 rounded-lg bg-muted"><p className="text-[10px] text-muted-foreground/70 uppercase mb-0.5">Employer</p><p className="text-sm text-foreground">{contract.parties.employer.name || contract.parties.employer}</p></div>}
                  {contract.parties.contractor && <div className="p-3 rounded-lg bg-muted"><p className="text-[10px] text-muted-foreground/70 uppercase mb-0.5">Contractor</p><p className="text-sm text-foreground">{contract.parties.contractor.name || contract.parties.contractor}</p></div>}
                </div>
              </div>
            )}
            {contract.documents?.length > 0 && (
              <div className="pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground/70 uppercase mb-2 tracking-wider">Documents ({contract.documents.length})</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto" data-testid="overview-docs-catalogue">
                  {contract.documents.map((d, i) => {
                    const docScore = d.cognara_score ?? d.ai_risk_score;
                    const docRisks = d.risk_count ?? d.ai_risk_count;
                    return (
                      <div key={i} className="p-2 rounded bg-muted" data-testid={`overview-doc-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1"><FileText className="w-3.5 h-3.5 text-blue-400 shrink-0" /><div className="min-w-0"><p className="text-xs text-foreground truncate">{d.file_name}</p><p className="text-[10px] text-muted-foreground/70">{formatDate(d.uploaded_at)} · {d.mime_type?.split('/').pop()?.toUpperCase()}</p></div></div>
                          {d.embeddings_generated ? <Badge className="bg-emerald-500/20 text-emerald-400 text-[9px] shrink-0">Indexed</Badge> : <Badge className="bg-amber-500/20 text-amber-400 text-[9px] shrink-0">Pending</Badge>}
                        </div>
                        {(docScore != null || docRisks != null) && (
                          <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                            {docScore != null && <span className="flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" style={{ color: scoreColor(docScore) }} /><span style={{ color: scoreColor(docScore) }}>Cognara {docScore}</span></span>}
                            {docRisks != null && <span className="flex items-center gap-1 text-red-400"><ShieldAlert className="w-2.5 h-2.5" />{docRisks} risks</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Executive Summary + Risk score */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><Bot className="w-4 h-4 text-purple-400" />AI Executive Summary</CardTitle></CardHeader>
          <CardContent>
            {aiReview ? (
              <>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="text-center p-2 rounded bg-muted border" style={{ borderColor: `${scoreColor(cognaraScore)}44` }}>
                    <p className="text-[10px] text-muted-foreground/70 uppercase">Cognara</p>
                    <p className="text-3xl font-bold" style={{ color: scoreColor(cognaraScore) }}>{cognaraScore}</p>
                    <Badge style={{ background: `${scoreColor(cognaraScore)}22`, color: scoreColor(cognaraScore) }} className="text-[9px]">{aiReview.verdict}</Badge>
                  </div>
                  <div className="text-center p-2 rounded bg-muted border" style={{ borderColor: `${riskColor(ai.risk_score)}44` }}>
                    <p className="text-[10px] text-muted-foreground/70 uppercase">Risk</p>
                    <p className="text-3xl font-bold" style={{ color: riskColor(ai.risk_score) }}>{ai.risk_score ?? '—'}</p>
                    <p className="text-[9px] text-muted-foreground/70">{risks.length} risks</p>
                  </div>
                </div>
                {aiReview.executiveSummary && <p className="text-xs text-foreground/80 leading-relaxed max-h-40 overflow-y-auto" data-testid="overview-exec-summary">{aiReview.executiveSummary}</p>}
                <Button size="sm" variant="ghost" className="w-full mt-3 text-purple-400 hover:text-purple-300" onClick={() => onGoto('ai-review')} data-testid="overview-open-ai-review">
                  Full Cognara Review <ArrowUpRight className="w-3 h-3 ml-1" />
                </Button>
              </>
            ) : (
              <div className="text-center py-8">
                <Sparkles className="w-8 h-8 text-muted-foreground/60 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">Run Cognara AI Review to see risks · mitigations · negotiation levers</p>
                <Button size="sm" onClick={() => onGoto('ai-review')} className="bg-purple-600 hover:bg-purple-700 text-foreground" data-testid="overview-run-ai-review">
                  <Sparkles className="w-3 h-3 mr-1" />Run Review
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Red Flags */}
      {(aiReview?.topRedFlags?.length > 0) && (
        <Card className="bg-card border-border" data-testid="overview-red-flags">
          <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-400" />Top Red Flags</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {aiReview.topRedFlags.map((rf, i) => (
                <div key={i} className="flex items-start gap-2 p-3 rounded" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
                  <Badge style={{ background: sev(rf.severity).bg, color: sev(rf.severity).color }} className="text-[9px] shrink-0">{rf.severity}</Badge>
                  <div className="flex-1 min-w-0"><p className="text-sm text-foreground font-semibold">{rf.title}</p><p className="text-xs text-muted-foreground mt-0.5">{rf.why}</p></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Claim opportunities scanned out of the selected documents */}
      {claimOpps.length > 0 && (
        <Card className="bg-card border-border" data-testid="overview-claim-opportunities">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-foreground text-base flex items-center gap-2">
              <Gavel className="w-4 h-4 text-amber-400" />Claim Opportunities ({claimOpps.length})
            </CardTitle>
            <Button size="sm" variant="ghost" className="text-purple-400 hover:text-purple-300"
                    onClick={() => onGoto('ai-review')} data-testid="overview-claim-opps-open-review">
              Open in Cognara AI Review <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-[11px] text-muted-foreground mb-2">
              Entitlements Cognara found in the documents you selected — {claimOpps.filter(c => !c.alreadyRaised).length} not yet raised.
            </p>
            <div className="max-h-[28rem] overflow-auto">
              <ClaimOpportunitiesTable items={claimOpps} onShowSource={setSourceRisk} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* LD · Variations · Disputes analysis from the same run */}
      {(ldAnalysis || variationsAnalysis || disputesAnalysis) && (
        <div className="grid grid-cols-1 gap-4">
          {ldAnalysis && (
            <Card className="bg-card border-border" data-testid="overview-ld-analysis">
              <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><Timer className="w-4 h-4 text-red-400" />Liquidated Damages Analysis</CardTitle></CardHeader>
              <CardContent><LdAnalysisPanel ld={ldAnalysis} /></CardContent>
            </Card>
          )}
          {variationsAnalysis && (
            <Card className="bg-card border-border" data-testid="overview-variations-analysis">
              <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><GitBranch className="w-4 h-4 text-violet-400" />Variations Analysis</CardTitle></CardHeader>
              <CardContent><VariationsAnalysisPanel va={variationsAnalysis} /></CardContent>
            </Card>
          )}
          {disputesAnalysis && (
            <Card className="bg-card border-border" data-testid="overview-disputes-analysis">
              <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><Scale className="w-4 h-4 text-orange-400" />Disputes Analysis</CardTitle></CardHeader>
              <CardContent><DisputesAnalysisPanel da={disputesAnalysis} /></CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Risk register + heat-map (side-by-side) */}
      {risks.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <Card className="bg-card border-border" data-testid="overview-risks">
            <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-orange-400" />Risk Register with Mitigations ({risks.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground uppercase text-[10px] sticky top-0 bg-card">
                    <tr>{['ID', 'Risk', 'Cat', 'L', 'I', 'Score', 'Doc · Clause', 'Exposure', 'Mitigation', 'Owner', 'Pri', ''].map(h => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {risks.slice(0, 40).map(r => {
                      const hasSource = !!(r.documentReference || r.clauseReference || r.quotedText);
                      return (
                      <tr key={r.id} className="border-t border-border" data-testid={`overview-risk-${r.id}`}>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{r.id}</td>
                        <td className="px-2 py-1.5 text-foreground">{r.title}</td>
                        <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{r.category}</Badge></td>
                        <td className="px-2 py-1.5 text-center">{r.likelihood}</td>
                        <td className="px-2 py-1.5 text-center">{r.impact}</td>
                        <td className="px-2 py-1.5 text-center font-semibold" style={{ color: r.score >= 15 ? '#EF4444' : r.score >= 8 ? '#F59E0B' : '#22C55E' }}>{r.score}</td>
                        <td className="px-2 py-1.5 text-[10px]">
                          {r.documentReference && <div className="text-blue-400 truncate max-w-[140px]" title={r.documentReference}>{r.documentReference}</div>}
                          {r.clauseReference && <div className="text-muted-foreground">Cl. {r.clauseReference}</div>}
                          {!r.documentReference && !r.clauseReference && <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-foreground/80 max-w-[180px]">
                          <div className="truncate" title={r.exposure}>{r.exposure}</div>
                          {r.quotedText && <div className="text-[9px] italic text-muted-foreground truncate mt-0.5" title={r.quotedText}>"{r.quotedText}"</div>}
                        </td>
                        <td className="px-2 py-1.5 text-foreground/80 max-w-[200px] truncate" title={r.mitigation}>{r.mitigation}</td>
                        <td className="px-2 py-1.5 text-muted-foreground">{r.owner}</td>
                        <td className="px-2 py-1.5"><Badge style={{ background: sev(r.priority).bg, color: sev(r.priority).color }} className="text-[9px]">{r.priority}</Badge></td>
                        <td className="px-2 py-1.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-[10px] text-purple-400 hover:text-purple-300 disabled:opacity-30"
                            disabled={!hasSource}
                            onClick={() => setSourceRisk(r)}
                            data-testid={`overview-risk-show-source-${r.id}`}
                            title={hasSource ? 'Open the clause paragraph in the source PDF' : 'No traceability data available'}
                          >
                            <MapPin className="w-3 h-3 mr-1" />Show source
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="overview-heatmap">
            <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" />Risk Heat-Map</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {heat.length === 0 && <p className="text-muted-foreground/70 text-xs">No categorised risks yet</p>}
              {heat.map(h => (
                <div key={h.category}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-foreground/80">{h.category}</span>
                    <span className="text-muted-foreground/70">{h.count} · avg {h.avgScore}</span>
                  </div>
                  <Progress value={Math.min(100, h.avgScore * 4)} className="h-1.5" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Negotiation levers + Missing clauses (side-by-side) */}
      {(levers.length > 0 || clausesGaps.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {levers.length > 0 && (
            <Card className="bg-card border-border" data-testid="overview-levers">
              <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><Target className="w-4 h-4 text-blue-400" />Negotiation Levers ({levers.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {levers.map((l, i) => (
                  <div key={i} className="p-3 rounded text-xs" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
                    <p className="font-semibold text-foreground mb-1">{l.lever}</p>
                    <div className="grid grid-cols-2 gap-2 text-foreground/80">
                      <div><span className="text-muted-foreground/70">Current:</span> {l.currentPosition}</div>
                      <div><span className="text-muted-foreground/70">Target:</span> {l.targetPosition}</div>
                    </div>
                    {l.batna && <div className="mt-1 text-foreground/80"><span className="text-muted-foreground/70">BATNA:</span> {l.batna}</div>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {clausesGaps.length > 0 && (
            <Card className="bg-card border-border" data-testid="overview-clauses-gaps">
              <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><FileWarning className="w-4 h-4 text-amber-400" />Missing / Weak Clauses ({clausesGaps.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {clausesGaps.map((c, i) => (
                  <div key={i} className="p-2 rounded text-xs" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="font-semibold text-foreground">{c.clause}</span>
                      <Badge style={{ background: c.status === 'MISSING' ? '#EF444422' : '#F59E0B22', color: c.status === 'MISSING' ? '#F87171' : '#FBBF24' }} className="text-[9px]">{c.status}</Badge>
                    </div>
                    <p className="text-foreground/80">{c.recommendedLanguage}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Claims exposure + Cashflow + Compliance + Actions */}
      {(claimsExp.length > 0 || cashflow || compliance || actions.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {claimsExp.length > 0 && (
            <Card className="bg-card border-border" data-testid="overview-claims-exp">
              <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><Scale className="w-4 h-4 text-orange-400" />Claims Exposure Matrix</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground uppercase text-[10px]"><tr>{['Clause', 'Deadline', 'Trigger', 'Est. Value', 'Recovery %'].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
                  <tbody>{claimsExp.map((c, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1.5 text-foreground">{c.clause}</td>
                      <td className="px-2 py-1.5">{c.noticeDeadlineDays}d</td>
                      <td className="px-2 py-1.5 text-foreground/80">{c.triggerEvent}</td>
                      <td className="px-2 py-1.5 text-foreground/80">{c.estimatedValue}</td>
                      <td className="px-2 py-1.5">{c.recoveryProbabilityPct}%</td>
                    </tr>
                  ))}</tbody>
                </table>
              </CardContent>
            </Card>
          )}
          {(cashflow || compliance) && (
            <Card className="bg-card border-border" data-testid="overview-cashflow">
              <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><TrendingDown className="w-4 h-4 text-emerald-400" />Cashflow & Compliance</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {cashflow && (
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(cashflow).map(([k, v]) => (
                      <div key={k} className="rounded p-2 text-xs" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
                        <p className="text-[10px] text-muted-foreground/70 uppercase mb-0.5">{k}</p>
                        <p className="text-foreground">{typeof v === 'string' ? v : JSON.stringify(v)}</p>
                      </div>
                    ))}
                  </div>
                )}
                {compliance && (
                  <div className="pt-2 border-t border-border">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-foreground">{compliance.framework}</span>
                      <span className="text-xs" style={{ color: scoreColor(compliance.score) }}>{compliance.score}/100</span>
                    </div>
                    {compliance.gaps?.length > 0 && <ul className="list-disc list-inside text-[10px] text-muted-foreground space-y-0.5">{compliance.gaps.slice(0, 4).map((g, i) => <li key={i}>{typeof g === 'string' ? g : JSON.stringify(g)}</li>)}</ul>}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Recommended Actions */}
      {actions.length > 0 && (
        <Card className="bg-card border-border" data-testid="overview-actions">
          <CardHeader className="pb-3"><CardTitle className="text-foreground text-base flex items-center gap-2"><ClipboardList className="w-4 h-4 text-purple-400" />Recommended Actions ({actions.length})</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-xs">
              <thead className="text-muted-foreground uppercase text-[10px]"><tr>{['Action', 'Priority', 'Due', 'Owner'].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
              <tbody>{actions.map((a, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-2 py-1.5 text-foreground">{a.action}</td>
                  <td className="px-2 py-1.5"><Badge style={{ background: sev(a.priority).bg, color: sev(a.priority).color }} className="text-[9px]">{a.priority}</Badge></td>
                  <td className="px-2 py-1.5">{a.dueDays}d</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{a.owner}</td>
                </tr>
              ))}</tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Cross-links to tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Clauses', value: clauses.length, icon: BookOpen, color: '#143A6B', tab: 'clauses' },
          { label: 'Obligations', value: obligations.length, icon: ClipboardList, color: '#3b82f6', tab: 'obligations', sub: rollup.overdue_obligations ? `${rollup.overdue_obligations} overdue` : '' },
          { label: 'Claims', value: claims.length, icon: Gavel, color: '#f59e0b', tab: 'claims' },
          { label: 'LD Trackers', value: ldTrackers.length, icon: Timer, color: '#ef4444', tab: 'ld' },
          { label: 'Variations', value: variations.length, icon: GitBranch, color: '#143A6B', tab: 'variations' },
          { label: 'Disputes', value: disputes.length, icon: Scale, color: '#f97316', tab: 'disputes' },
          { label: 'Letters', value: correspondences.length, icon: Mail, color: '#06b6d4', tab: 'correspondences', sub: rollup.urgent_correspondences ? `${rollup.urgent_correspondences} urgent` : '' },
        ].map((k) => (
          <Card key={k.label} onClick={() => onGoto(k.tab)} className="bg-card border-border cursor-pointer hover:border-purple-500/60 transition-all" data-testid={`overview-nav-${k.tab}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded" style={{ backgroundColor: `${k.color}20` }}><k.icon className="w-4 h-4" style={{ color: k.color }} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground/70">{k.label}</p>
                <p className="text-xl font-bold text-foreground">{k.value}</p>
                {k.sub && <p className="text-[9px] text-red-400">{k.sub}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Show-source drilldown — expands the exact clause paragraph inline */}
      <RiskSourceDialog
        open={!!sourceRisk}
        onOpenChange={(o) => !o && setSourceRisk(null)}
        risk={sourceRisk}
        contractId={contractId}
        documents={contract?.documents || []}
      />
    </div>
    </CognaraReviewProvider>
  );
}

function Field({ label, value }) {
  return <div><p className="text-[10px] text-muted-foreground/70 uppercase mb-0.5 tracking-wider">{label}</p><div className="text-sm text-foreground">{value ?? '—'}</div></div>;
}

export default ContractOverview;
